import {useState} from 'react';

import {useCallbackWithRef} from '@pb/kit-util-use-callback-with-ref';

/**
 * `useControlledState` is a replacement for the ordinary `useState`, with the ability to optionally update the state from props.
 *
 * Think of it as a state manager for a component, that can be either controlled or uncontrolled.
 *
 * In case the props are not provided, the state will be managed internally.
 *
 * In case only the `set` function is provided, the state will be managed internally, but the `set` function will be called when the state is updated.
 *
 * In case both the `value` and the `set` function are provided, the state will be managed externally, and the `set` function will be called when the state is updated.
 * */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useControlledState = <S = unknown, T extends ((arg0: S, ...argN: any[]) => void) = ((arg0: S) => void)>(
    externalValue?: S,
    externalSetter?: T,
    initialInternalValue?: S,
): [S, T] => {
    const [internalValue, internalSetter] = useState<S>((externalValue ?? initialInternalValue) as S);
    const setter = useCallbackWithRef(((...args) => {
        internalSetter(args[0]);
        externalSetter?.(...args);
    }) as T);
    return [(externalValue === undefined ? internalValue : externalValue) as S, setter];
};
